import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Question, UserAnswer, QuizResult, QuizMode, CustomQuizConfig, User, SavedQuizProgress } from '../types';
import QuestionCard from './QuestionCard';
import QuizControls from './QuizControls';
import QuestionNavigation from './QuestionNavigation';
import Timer from './Timer';
import ConfirmationModal from './ConfirmationModal';
import SuccessModal from './SuccessModal';
import ProgressBar from './ProgressBar';
import apiClient from '../auth/service';
import { debounce } from '../utils/debounce';


interface QuizProps {
    onFinish: (results: QuizResult) => void;
    resume: boolean;
    mode: QuizMode;
    customConfig?: CustomQuizConfig;
    allQuestions: Question[];
    currentUser: User;
    examDurationSeconds?: number;
    questionCountOverride?: number;
    passingScore?: number;
    displayLanguage: 'pt' | 'en' | 'es';
}

const getExamDurationFromProps = (override?: number): number => {
    if (typeof override === 'number' && override > 0) return override;
    return 130 * 60;
};

const Quiz: React.FC<QuizProps> = ({ onFinish, resume, mode, customConfig, allQuestions, currentUser, examDurationSeconds, questionCountOverride, passingScore, displayLanguage }) => {
    
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [userAnswers, setUserAnswers] = useState<Map<number, UserAnswer>>(new Map());
    const [markedForReview, setMarkedForReview] = useState<Set<number>>(new Set());
    const [startTime, setStartTime] = useState(Date.now());
    const [timeLeft, setTimeLeft] = useState(getExamDurationFromProps(examDurationSeconds));
    const [showFinishModal, setShowFinishModal] = useState(false);
    const [isInReviewPhase, setIsInReviewPhase] = useState(false);
    const [isNavVisible, setIsNavVisible] = useState(false);
    const [timePerQuestion, setTimePerQuestion] = useState<Map<number, number>>(new Map());
    const [questionStartTime, setQuestionStartTime] = useState(Date.now());
    const prevQuestionIndexRef = useRef<number>(currentQuestionIndex);
    const isCustomQuiz = !!customConfig;
    const [isLoading, setIsLoading] = useState(true);
    const [initError, setInitError] = useState<string | null>(null);
    const [showTrialExpired, setShowTrialExpired] = useState(false);

    /** Idioma só para rótulos na UI; não pode disparar nova inicialização do simulado (embaralhamento). */
    const displayLanguageRef = useRef(displayLanguage);
    useEffect(() => {
        displayLanguageRef.current = displayLanguage;
    }, [displayLanguage]);

    const saveProgress = useMemo(() => 
        debounce(async (progress: SavedQuizProgress) => {
            if (isCustomQuiz) return; // Don't save progress for custom quizzes
            try {
                await apiClient.post('/progress', { progress });
            } catch (error) {
                console.error("Failed to save progress:", error);
            }
        }, 1000), 
    [isCustomQuiz]);

    useEffect(() => {
        const initializeQuiz = async () => {
            const lang = displayLanguageRef.current;
            if (allQuestions.length === 0) {
                setInitError(lang === 'en' ? 'No questions loaded. Please try again.' : lang === 'es' ? 'No hay preguntas cargadas. Intenta nuevamente.' : 'Nenhuma questão carregada. Tente novamente.');
                setIsLoading(false);
                return;
            }
            setIsLoading(true);

            const shuffleArray = (array: any[]) => {
                for (let i = array.length - 1; i > 0; i--) {
                    const j = Math.floor(Math.random() * (i + 1));
                    [array[i], array[j]] = [array[j], array[i]];
                }
                return array;
            };

            if (resume) {
                try {
                    const data = await apiClient.get('/progress');
                    if (data.progress && data.progress.mode === mode) {
                        const savedProgress = data.progress as SavedQuizProgress;
                        setQuestions(savedProgress.questions);
                        setCurrentQuestionIndex(savedProgress.currentQuestionIndex);
                        setUserAnswers(new Map<number, UserAnswer>(Object.entries(savedProgress.userAnswers).map(([k,v]) => [Number(k), v as UserAnswer])));
                        setMarkedForReview(new Set<number>(savedProgress.markedForReview));
                        setIsInReviewPhase(savedProgress.isInReviewPhase || false);
                        setTimePerQuestion(new Map<number, number>(Object.entries(savedProgress.timePerQuestion || {}).map(([k, v]) => [Number(k), v as number])));
                        if (mode === 'exam') {
                            setTimeLeft(savedProgress.timeLeft);
                        }
                        setQuestionStartTime(Date.now());
                        setIsLoading(false);
                        return;
                    }
                } catch(error: any) {
                    if (error?.message && (error.message.includes('Forbidden') || error.message.includes('403'))) {
                        setShowTrialExpired(true);
                        setInitError('Período de teste expirado. Contate o administrador.');
                        setIsLoading(false);
                        return;
                    }
                    console.error("Could not resume quiz from DB, starting new.", error);
                }
            }

            let selectedQuestions: Question[];
            let pool = allQuestions;
            if (isCustomQuiz && customConfig.domains.length > 0) {
                const filtered = allQuestions.filter(q => q.domain && customConfig.domains.includes(q.domain));
                pool = filtered.length > 0 ? filtered : allQuestions;
            }
            const shuffled = shuffleArray([...pool]);
            const count = isCustomQuiz
                ? Math.min(customConfig.questionCount, shuffled.length)
                : (typeof questionCountOverride === 'number' && questionCountOverride > 0 ? Math.min(questionCountOverride, shuffled.length) : shuffled.length);
            selectedQuestions = shuffled.slice(0, count);
            if (selectedQuestions.length === 0) {
                setInitError(lang === 'en' ? 'No questions available for selected domains.' : lang === 'es' ? 'No hay preguntas disponibles para los dominios seleccionados.' : 'Não há questões disponíveis para os domínios selecionados.');
                setIsLoading(false);
                return;
            }
            setQuestions(selectedQuestions);
            setCurrentQuestionIndex(0);
            setUserAnswers(new Map());
            setMarkedForReview(new Set());
            setIsInReviewPhase(false);
            setTimePerQuestion(new Map());
            if (mode === 'exam') setTimeLeft(getExamDurationFromProps(examDurationSeconds));
            setStartTime(Date.now());
            setQuestionStartTime(Date.now());
            setIsLoading(false);
        }
        initializeQuiz();
        // displayLanguage omitido de propósito: trocar idioma altera só rótulos da UI, não o conjunto/embaralhamento das questões.
    }, [resume, mode, customConfig, isCustomQuiz, allQuestions]);

    useEffect(() => {
        const prevIndex = prevQuestionIndexRef.current;
        if (prevIndex !== currentQuestionIndex) {
            const prevQuestion = questions[prevIndex];
            if (prevQuestion) {
                const timeSpent = Math.floor((Date.now() - questionStartTime) / 1000);
                setTimePerQuestion(prevMap => {
                    const newMap = new Map<number, number>(prevMap);
                    const existingTime = newMap.get(prevQuestion.id) || 0;
                    newMap.set(prevQuestion.id, existingTime + timeSpent);
                    return newMap;
                });
            }
            setQuestionStartTime(Date.now());
        }
        prevQuestionIndexRef.current = currentQuestionIndex;
    }, [currentQuestionIndex, questions, questionStartTime]);

    useEffect(() => {
        if (questions.length > 0 && !isCustomQuiz) {
            const progress: SavedQuizProgress = {
                questions,
                currentQuestionIndex,
                userAnswers: Object.fromEntries(userAnswers),
                markedForReview: Array.from(markedForReview),
                timeLeft,
                mode,
                isInReviewPhase,
                timePerQuestion: Object.fromEntries(timePerQuestion),
            };
            saveProgress(progress);
        }
    }, [questions, currentQuestionIndex, userAnswers, markedForReview, timeLeft, mode, isInReviewPhase, timePerQuestion, isCustomQuiz, saveProgress]);

    const handleAnswerSelect = (questionId: number, selectedOptions: string[]) => {
        const question = questions.find(q => q.id === questionId);
        if (!question) return;

        const isCorrect = question.correctAnswers.length === selectedOptions.length &&
                          question.correctAnswers.every(answer => selectedOptions.includes(answer));

        const newAnswers = new Map(userAnswers);
        newAnswers.set(questionId, { selected: selectedOptions, isCorrect });
        setUserAnswers(newAnswers);
    };

    const handleToggleReview = (questionId: number) => {
        const newMarked = new Set(markedForReview);
        if (newMarked.has(questionId)) {
            newMarked.delete(questionId);
        } else {
            newMarked.add(questionId);
        }
        setMarkedForReview(newMarked);
    };

    const handleFinishAttempt = () => setShowFinishModal(true);

    const handleConfirmFinish = useCallback(() => {
        const finalTimeMap = new Map<number, number>(timePerQuestion);
        const lastQuestion = questions[currentQuestionIndex];
        if (lastQuestion) {
             const timeSpent = Math.floor((Date.now() - questionStartTime) / 1000);
             const existingTime = finalTimeMap.get(lastQuestion.id) || 0;
             finalTimeMap.set(lastQuestion.id, existingTime + timeSpent);
        }

        const examDuration = getExamDurationFromProps(examDurationSeconds);
        const timeTaken = mode === 'exam' ? examDuration - timeLeft : Math.floor((Date.now() - startTime) / 1000);
        const finalAnswers = new Map<number, UserAnswer>();
        questions.forEach(q => {
            const userAnswer = userAnswers.get(q.id);
            finalAnswers.set(q.id, userAnswer || { selected: [], isCorrect: false });
        });

        const results: QuizResult = {
            answers: finalAnswers, questions, timeTaken, mode, timePerQuestion: finalTimeMap, customConfig,
            passingScore,
        };
        setShowFinishModal(false);
        onFinish(results);
    }, [onFinish, userAnswers, questions, startTime, timeLeft, mode, timePerQuestion, currentQuestionIndex, questionStartTime, customConfig]);

    const handleCancelFinish = () => setShowFinishModal(false);
    const handleTimeUp = useCallback(() => handleConfirmFinish(), [handleConfirmFinish]);
    const handleTick = useCallback((secondsLeft: number) => setTimeLeft(secondsLeft), []);

    const markedQuestionIndices = useMemo(() => {
        return questions.map((q, i) => (markedForReview.has(q.id) ? i : -1)).filter(i => i !== -1);
    }, [questions, markedForReview]);

    const handleNext = () => {
        const isLastRegular = !isInReviewPhase && currentQuestionIndex === questions.length - 1;
        if (isLastRegular && markedQuestionIndices.length > 0) {
            setIsInReviewPhase(true);
            setCurrentQuestionIndex(markedQuestionIndices[0]);
        } else if (isInReviewPhase) {
            const currentPosition = markedQuestionIndices.indexOf(currentQuestionIndex);
            if (currentPosition < markedQuestionIndices.length - 1) {
                setCurrentQuestionIndex(markedQuestionIndices[currentPosition + 1]);
            } else {
                handleFinishAttempt();
            }
        } else {
            if (currentQuestionIndex < questions.length - 1) {
                setCurrentQuestionIndex(currentQuestionIndex + 1);
            } else {
                handleFinishAttempt();
            }
        }
    };

    const handlePrev = () => {
        if (isInReviewPhase) {
            const currentPosition = markedQuestionIndices.indexOf(currentQuestionIndex);
            if (currentPosition > 0) {
                setCurrentQuestionIndex(markedQuestionIndices[currentPosition - 1]);
            }
        } else {
            if (currentQuestionIndex > 0) {
                setCurrentQuestionIndex(currentQuestionIndex - 1);
            }
        }
    };

    const handleJumpToQuestion = (index: number) => {
        if (isInReviewPhase && !markedForReview.has(questions[index].id)) {
            setIsInReviewPhase(false);
        }
        setCurrentQuestionIndex(index);
    };
    
    const toggleNavVisibility = () => setIsNavVisible(prev => !prev);

    if (isLoading) {
        return <div className="text-center text-xl p-8 text-gray-700 dark:text-slate-200">{displayLanguage === 'en' ? 'Loading quiz...' : displayLanguage === 'es' ? 'Cargando examen...' : 'Carregando simulado...'}</div>;
    }
    if (initError) {
        return <div className="text-center text-xl p-8 text-red-600 dark:text-red-400">{initError}</div>;
    }

    const currentQuestion = questions[currentQuestionIndex];
    if (!currentQuestion) {
        return <div className="text-center text-xl p-8 text-gray-700 dark:text-slate-200">{displayLanguage === 'en' ? 'Loading question...' : displayLanguage === 'es' ? 'Cargando pregunta...' : 'Carregando questão...'}</div>;
    }
    const userAnswer = userAnswers.get(currentQuestion.id);
    const isLastInRegular = !isInReviewPhase && currentQuestionIndex === questions.length - 1;
    const isLastInReview = isInReviewPhase && markedQuestionIndices.indexOf(currentQuestionIndex) === markedQuestionIndices.length - 1;
    const effectiveMode = isCustomQuiz ? 'practice' : mode;
    const modeText = isCustomQuiz
        ? (displayLanguage === 'en' ? 'Custom' : displayLanguage === 'es' ? 'Personalizado' : 'Personalizado')
        : (mode === 'practice'
            ? (displayLanguage === 'en' ? 'Practice' : displayLanguage === 'es' ? 'Práctica' : 'Prática')
            : (displayLanguage === 'en' ? 'Exam' : displayLanguage === 'es' ? 'Examen' : 'Simulado'));
    const modeColor = isCustomQuiz ? 'text-purple-400' : (mode === 'practice' ? 'text-orange-400' : 'text-cyan-400');

    return (
        <>
            <ConfirmationModal isOpen={showFinishModal} onConfirm={handleConfirmFinish} onCancel={handleCancelFinish} title={displayLanguage === 'en' ? 'Finish Exam?' : displayLanguage === 'es' ? 'Finalizar examen?' : 'Finalizar Exame?'}>
                {displayLanguage === 'en' ? 'Are you sure you want to finish and submit your answers?' : displayLanguage === 'es' ? '¿Seguro que deseas finalizar y enviar tus respuestas?' : 'Você tem certeza que deseja finalizar e enviar suas respostas?'}
            </ConfirmationModal>
            <SuccessModal isOpen={showTrialExpired} onConfirm={() => setShowTrialExpired(false)} title={displayLanguage === 'en' ? 'Trial Period Expired' : displayLanguage === 'es' ? 'Período de prueba expirado' : 'Período de Teste Expirado'} buttonText={displayLanguage === 'en' ? 'Close' : displayLanguage === 'es' ? 'Cerrar' : 'Fechar'}>
                {displayLanguage === 'en' ? 'Your trial period has ended. Contact the administrator for full access.' : displayLanguage === 'es' ? 'Tu período de prueba terminó. Contacta al administrador para acceso completo.' : 'Seu período de teste terminou. Contate o administrador para liberar acesso completo.'}
            </SuccessModal>
            <div className="flex flex-col h-full animate-fade-in w-full">
                <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-lg mb-6 sticky top-4 z-10">
                    <div className="flex justify-between items-center flex-wrap gap-4 mb-4">
                        <div className="flex items-center gap-4">
                             <div className="font-bold">{displayLanguage === 'en' ? 'Mode' : displayLanguage === 'es' ? 'Modo' : 'Modo'}: <span className={modeColor}>{modeText}</span></div>
                             <button onClick={toggleNavVisibility} className="text-gray-500 hover:text-gray-900 dark:text-slate-400 dark:hover:text-white transition-colors text-lg" title={isNavVisible ? (displayLanguage === 'en' ? 'Hide Navigation' : displayLanguage === 'es' ? 'Ocultar navegación' : 'Ocultar Navegação') : (displayLanguage === 'en' ? 'Show Navigation' : displayLanguage === 'es' ? 'Mostrar navegación' : 'Mostrar Navegação')} aria-label={isNavVisible ? (displayLanguage === 'en' ? 'Hide Navigation' : displayLanguage === 'es' ? 'Ocultar navegación' : 'Ocultar Navegação') : (displayLanguage === 'en' ? 'Show Navigation' : displayLanguage === 'es' ? 'Mostrar navegación' : 'Mostrar Navegação')}>
                                <i className={`fas ${isNavVisible ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                            </button>
                        </div>
                        {mode === 'exam' && !isCustomQuiz && <Timer initialSeconds={timeLeft} onTimeUp={handleTimeUp} onTick={handleTick} />}
                        <div className="text-lg font-bold">{displayLanguage === 'en' ? 'Question' : displayLanguage === 'es' ? 'Pregunta' : 'Questão'} {currentQuestionIndex + 1}/{questions.length}</div>
                    </div>
                     <ProgressBar current={currentQuestionIndex + 1} total={questions.length} />
                </div>
                {isNavVisible && (
                    <QuestionNavigation questions={questions} userAnswers={userAnswers} markedForReview={markedForReview} currentQuestionIndex={currentQuestionIndex} onJumpToQuestion={handleJumpToQuestion} displayLanguage={displayLanguage} />
                )}
                <QuestionCard question={currentQuestion} userAnswer={userAnswer} onAnswerSelect={handleAnswerSelect} isMarkedForReview={markedForReview.has(currentQuestion.id)} onToggleReview={handleToggleReview} isExamMode={effectiveMode === 'exam'} displayLanguage={displayLanguage} />
                {questions.length > 1 && (
                     <QuizControls onPrev={handlePrev} onNext={handleNext} isFirst={(!isInReviewPhase && currentQuestionIndex === 0) && (markedQuestionIndices.indexOf(currentQuestionIndex) <= 0)} isLast={isLastInReview || (isLastInRegular && markedQuestionIndices.length === 0)} isReviewing={isInReviewPhase} isLastInRegular={isLastInRegular} hasReviewItems={markedQuestionIndices.length > 0} displayLanguage={displayLanguage} />
                )}
            </div>
        </>
    );
};

export default Quiz;
